import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MobileLayout from '../components/MobileLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Star, 
  Plus, 
  Check, 
  Lock,
  Bell, 
  X, 
  Crown,
  ChevronRight,
  Sparkles,
  Gift,
  ArrowRight,
  Heart,
  Camera,
  Hand
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Challenge icons mapping - Only 3 manual challenges now
const CHALLENGE_ICONS = {
  photo_of_day: Camera,
  kiss_30sec: Heart,
  secret_handshake: Hand,
};

const BrowniesContent = ({ user }) => {
  // Onboarding state
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    return localStorage.getItem('browniesOnboardingSeen') === 'true';
  });
  
  // Main states
  const [status, setStatus] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [myGestures, setMyGestures] = useState([]);
  const [partnerGestures, setPartnerGestures] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI states
  const [showCreateGesture, setShowCreateGesture] = useState(false);
  const [showMyGestures, setShowMyGestures] = useState(false);
  const [showPartnerGestures, setShowPartnerGestures] = useState(false);
  const [showCompletion, setShowCompletion] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  
  // Form states
  const [newGestureTitle, setNewGestureTitle] = useState('');
  const [newGestureDesc, setNewGestureDesc] = useState('');
  const [newGesturePoints, setNewGesturePoints] = useState(25);
  
  const fetchData = useCallback(async () => {
    try {
      const [statusRes, challengesRes, tradesRes] = await Promise.all([
        axios.get(`${API}/browny/status`, { withCredentials: true }),
        axios.get(`${API}/browny/challenges`, { withCredentials: true }),
        axios.get(`${API}/browny/trades`, { withCredentials: true })
      ]);
      
      setStatus(statusRes.data);
      setChallenges(challengesRes.data.challenges);
      setMyGestures(tradesRes.data.my_trades);
      setPartnerGestures(tradesRes.data.partner_trades);
    } catch (error) {
      console.error('Error fetching browny data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (hasSeenOnboarding) {
      // Start trial if needed
      axios.post(`${API}/browny/start-trial`, {}, { withCredentials: true })
        .catch(console.error);
      fetchData();
    }
  }, [hasSeenOnboarding, fetchData]);
  
  const completeOnboarding = () => {
    localStorage.setItem('browniesOnboardingSeen', 'true');
    setHasSeenOnboarding(true);
  };
  
  const handleCompleteChallenge = async (challengeId, whoCompleted) => {
    try {
      const res = await axios.post(
        `${API}/browny/earn/${challengeId}`,
        {},
        { withCredentials: true, params: { who_completed: whoCompleted } }
      );
      
      toast.success(`+${res.data.points_awarded} points earned!`);
      setCelebrationData({
        points: res.data.points_awarded,
        bonus: 0
      });
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2500);
      setShowCompletion(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete challenge');
    }
  };
  
  const handleCreateGesture = async () => {
    if (!newGestureTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      await axios.post(
        `${API}/browny/trades`,
        {
          title: newGestureTitle.trim(),
          description: newGestureDesc.trim() || null,
          points_value: newGesturePoints
        },
        { withCredentials: true }
      );
      
      toast.success('Gesture created!');
      setShowCreateGesture(false);
      setNewGestureTitle('');
      setNewGestureDesc('');
      setNewGesturePoints(25);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gesture');
    }
  };
  
  const handleCompleteGesture = async (gestureId) => {
    try {
      const res = await axios.post(
        `${API}/browny/trades/${gestureId}/complete`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`+${res.data.points_earned} points earned!`);
      setCelebrationData({
        points: res.data.points_earned,
        bonus: 0
      });
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2500);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete gesture');
    }
  };
  
  const handleNudge = async (gestureId) => {
    try {
      await axios.post(
        `${API}/browny/trades/${gestureId}/nudge`,
        {},
        { withCredentials: true }
      );
      toast.success('Nudge sent to partner!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send nudge');
    }
  };
  
  const handleDeleteGesture = async (gestureId) => {
    try {
      await axios.delete(`${API}/browny/trades/${gestureId}`, { withCredentials: true });
      toast.success('Gesture deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete gesture');
    }
  };
  
  // ==================== ONBOARDING / INFO PAGE ====================
  if (!hasSeenOnboarding) {
    return (
      <MobileLayout>
        <div className="p-6 min-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="text-center space-y-6 animate-in fade-in duration-500">
            <div className="text-7xl mb-4">⭐</div>
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Browny Points</h1>
              <p className="text-lg text-gray-600 mt-2">Practical exchanges of gratitude</p>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl border border-pink-100 p-5 text-left max-w-xs mx-auto">
              <p className="text-gray-700 text-sm leading-relaxed">
                Browny Points are a fun way to show appreciation in your relationship. 
                <span className="font-semibold text-primary"> Earn</span> them together, 
                <span className="font-semibold text-primary"> spend</span> them on things you want from your partner!
              </p>
            </div>
            
            {/* Ways to Earn */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 max-w-xs mx-auto text-left">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                Ways to Earn Points
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">+20</span>
                  </div>
                  <p className="text-gray-600">Log your daily mood</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">+50</span>
                  </div>
                  <p className="text-gray-600">Both log mood same day</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">+25</span>
                  </div>
                  <p className="text-gray-600">Create a calendar event</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">+30</span>
                  </div>
                  <p className="text-gray-600">Complete fun challenges like &quot;Photo of the Day&quot;</p>
                </div>
              </div>
            </div>
            
            {/* What to Spend On */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 max-w-xs mx-auto text-left">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-500" />
                Spend Points On
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• Create custom gestures like foot massages</p>
                <p>• Ask your partner to make dinner</p>
                <p>• Request quality time activities</p>
                <p>• Anything you both agree on!</p>
              </div>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-3 max-w-xs mx-auto">
              <p className="text-sm text-yellow-800">
                🎉 <span className="font-semibold">Start earning together!</span>
              </p>
            </div>
            
            <Button
              onClick={completeOnboarding}
              className="w-full max-w-xs h-14 rounded-2xl gradient-brand shadow-pink text-white font-semibold mt-4"
            >
              Start Earning Points
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  if (loading || !status) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-120px)]">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </MobileLayout>
    );
  }
  
  // Check if user has partner - Strong CTA matching app style
  if (!status.partner) {
    return (
      <MobileLayout>
        <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-120px)]">
          <div className="text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-sm mx-auto">
            
            {/* Hero icon with glow effect */}
            <div className="relative">
              <div className="absolute inset-0 w-24 h-24 mx-auto bg-primary/20 rounded-full blur-2xl animate-pulse" />
              <div className="relative w-24 h-24 mx-auto gradient-brand rounded-full flex items-center justify-center shadow-lg">
                <Star className="w-12 h-12 text-white" fill="white" />
              </div>
            </div>
            
            {/* Title */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-gradient">Browny Points</h1>
              <p className="text-gray-500 text-sm">
                Turn appreciation into action
              </p>
            </div>
            
            {/* Feature card */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-soft text-left">
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Earn points together, spend them on meaningful gestures. Make your relationship more fun and rewarding!
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full gradient-brand-soft flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Earn Together</p>
                    <p className="text-xs text-gray-500">Log moods, complete challenges</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full gradient-brand-soft flex items-center justify-center">
                    <Gift className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Share Gestures</p>
                    <p className="text-xs text-gray-500">Foot massages, breakfast in bed...</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full gradient-brand-soft flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Grow Closer</p>
                    <p className="text-xs text-gray-500">Make appreciation tangible</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Trial badge */}
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Start earning together!
            </div>
            
            {/* CTA Button */}
            <Button
              onClick={() => window.location.href = '/connection'}
              className="w-full h-14 rounded-2xl gradient-brand shadow-pink text-white font-semibold text-base tap-feedback"
            >
              Connect with Partner
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <p className="text-gray-400 text-xs">
              Go to Settings to find your connection code
            </p>
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  // ==================== CELEBRATION OVERLAY ====================
  const renderCelebration = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl p-8 mx-6 text-center animate-in zoom-in duration-500">
        <Sparkles className="w-16 h-16 text-yellow-400 mx-auto animate-pulse" />
        <h2 className="text-2xl font-bold text-gray-900 mt-4">Points Earned!</h2>
        <div className="flex items-center justify-center gap-2 my-4">
          <Star className="w-8 h-8 text-yellow-500" fill="currentColor" />
          <span className="text-5xl font-bold text-gradient">+{celebrationData?.points}</span>
        </div>
      </div>
    </div>
  );
  
  // ==================== COMPLETION FLOW MODAL ====================
  const renderCompletionModal = () => {
    if (!showCompletion) return null;
    
    const challenge = challenges.find(c => c.id === showCompletion);
    if (!challenge) return null;
    
    // Check if this challenge requires "both only" (kiss_30sec, secret_handshake)
    const isBothOnly = challenge.id === 'kiss_30sec' || challenge.id === 'secret_handshake';
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center animate-in fade-in duration-200">
        <div className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-in slide-in-from-bottom duration-300 mb-[70px] max-h-[70vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-bold text-gray-900">{challenge.name}</h3>
            <button onClick={() => setShowCompletion(null)} className="p-2 -mr-1">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <p className="text-gray-500 text-sm mb-6">{challenge.description}</p>
          
          {isBothOnly ? (
            <>
              <p className="font-medium text-gray-700 mb-4">This is a couples activity!</p>
              <div className="space-y-3 pb-4">
                <button
                  onClick={() => handleCompleteChallenge(challenge.id, 'both')}
                  className="w-full p-4 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-colors text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full gradient-brand flex items-center justify-center">
                    <span className="text-lg">✨</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">We both completed it!</p>
                    <p className="text-sm text-primary">+{challenge.points} points each!</p>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="font-medium text-gray-700 mb-4">Who completed this?</p>
              <div className="space-y-3 pb-4">
                <button
                  onClick={() => handleCompleteChallenge(challenge.id, 'user')}
                  className="w-full p-4 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">👤</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">I completed it</p>
                    <p className="text-sm text-gray-500">+{challenge.points} points to you</p>
                  </div>
                </button>
                
                <button
                  onClick={() => handleCompleteChallenge(challenge.id, 'partner')}
                  className="w-full p-4 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                    <span className="text-lg">💕</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{status.partner.name.split(' ')[0]} completed it</p>
                    <p className="text-sm text-gray-500">+{challenge.points} points to them</p>
                  </div>
                </button>
                
                <button
                  onClick={() => handleCompleteChallenge(challenge.id, 'both')}
                  className="w-full p-4 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-colors text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full gradient-brand flex items-center justify-center">
                    <span className="text-lg">✨</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">We both completed it!</p>
                    <p className="text-sm text-primary">+{challenge.points} points each!</p>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };
  
  // ==================== CREATE GESTURE MODAL ====================
  const renderCreateGestureModal = () => {
    if (!showCreateGesture) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center animate-in fade-in duration-200">
        <div className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-in slide-in-from-bottom duration-300 mb-[70px] max-h-[70vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-bold text-gray-900">Create New Gesture</h3>
            <button onClick={() => setShowCreateGesture(false)} className="p-2 -mr-1">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <p className="text-gray-500 text-sm mb-4">
            Create a gesture you want your partner to do for you. When they complete it, you&apos;ll spend points and they&apos;ll earn them!
          </p>
          
          <div className="space-y-4 pb-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">What do you want?</label>
              <Input
                value={newGestureTitle}
                onChange={(e) => setNewGestureTitle(e.target.value)}
                placeholder="e.g., Foot massage, Make dinner..."
                className="rounded-xl"
                maxLength={50}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Description (optional)</label>
              <Textarea
                value={newGestureDesc}
                onChange={(e) => setNewGestureDesc(e.target.value)}
                placeholder="Add any details..."
                className="rounded-xl resize-none"
                rows={2}
                maxLength={200}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Points: {newGesturePoints}</label>
              <input
                type="range"
                min="5"
                max={status.is_premium ? 500 : 100}
                value={newGesturePoints}
                onChange={(e) => setNewGesturePoints(parseInt(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5</span>
                <span>{status.is_premium ? '500' : '100 (max for free)'}</span>
              </div>
            </div>
            
            <Button
              onClick={handleCreateGesture}
              className="w-full h-12 rounded-xl gradient-brand text-white font-semibold"
            >
              Create Gesture ({newGesturePoints} points)
            </Button>
          </div>
        </div>
      </div>
    );
  };
  
  // ==================== MY GESTURES DRAWER ====================
  const renderMyGesturesDrawer = () => {
    if (!showMyGestures) return null;
    
    const visibleGestures = status.is_premium || status.is_trial_active 
      ? myGestures 
      : myGestures.slice(0, 1);
    const lockedGestures = status.is_premium || status.is_trial_active 
      ? [] 
      : myGestures.slice(1);
    
    // Check if user has no gestures yet (for glow effect on create button)
    const hasNoGestures = myGestures.length === 0;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center animate-in fade-in duration-200">
        <div className="bg-white rounded-t-3xl w-full max-h-[70vh] overflow-hidden animate-in slide-in-from-bottom duration-300 mb-[70px]">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
            <h3 className="text-lg font-bold text-gray-900">Spend Brownies</h3>
            <button onClick={() => setShowMyGestures(false)} className="p-2 -mr-1">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[calc(70vh-60px)] pb-6">
            <p className="text-gray-500 text-sm mb-4">
              Gestures you want {status.partner.name.split(' ')[0]} to do for you
            </p>
            
            {/* Create new button - GLOWS when no gestures yet */}
            {(status.is_premium || status.is_trial_active || myGestures.length === 0) && (
              <button
                onClick={() => {
                  setShowMyGestures(false);
                  setShowCreateGesture(true);
                }}
                className={`w-full p-4 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-2 mb-4 ${
                  hasNoGestures 
                    ? 'border-primary bg-primary/10 animate-pulse shadow-lg shadow-primary/20' 
                    : 'border-gray-200 hover:border-primary hover:bg-primary/5'
                }`}
              >
                <Plus className={`w-5 h-5 ${hasNoGestures ? 'text-primary' : 'text-gray-400'}`} />
                <span className={`font-medium ${hasNoGestures ? 'text-primary' : 'text-gray-600'}`}>
                  {hasNoGestures ? 'Create Your First Gesture!' : 'Create New Gesture'}
                </span>
              </button>
            )}
            
            {/* Visible gestures */}
            <div className="space-y-3">
              {visibleGestures.map((gesture) => (
                <div
                  key={gesture.trade_id}
                  className={`p-4 rounded-xl border ${
                    gesture.is_glowing 
                      ? 'border-yellow-400 bg-yellow-50 animate-pulse' 
                      : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{gesture.title}</h4>
                      {gesture.description && (
                        <p className="text-sm text-gray-500 mt-1">{gesture.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 bg-yellow-100 px-2 py-1 rounded-full">
                      <Star className="w-3 h-3 text-yellow-600" fill="currentColor" />
                      <span className="text-sm font-bold text-yellow-700">{gesture.points_value}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                    <span>Completed {gesture.times_completed}x</span>
                    <span>{gesture.total_points_earned} pts earned total</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleCompleteGesture(gesture.trade_id)}
                      size="sm"
                      className="flex-1 h-9 rounded-lg gradient-brand text-white"
                      disabled={status.user_points < gesture.points_value}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Mark Done
                    </Button>
                    <Button
                      onClick={() => handleNudge(gesture.trade_id)}
                      size="sm"
                      variant="outline"
                      className="h-9 rounded-lg"
                    >
                      <Bell className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteGesture(gesture.trade_id)}
                      size="sm"
                      variant="outline"
                      className="h-9 rounded-lg text-red-500 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {status.user_points < gesture.points_value && (
                    <p className="text-xs text-red-500 mt-2">
                      Not enough points! Complete earn tasks first.
                    </p>
                  )}
                </div>
              ))}
            </div>
            
            {/* Locked gestures */}
            {lockedGestures.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Locked Gestures</span>
                </div>
                <div className="space-y-3 opacity-50 blur-[2px]">
                  {lockedGestures.map((gesture) => (
                    <div key={gesture.trade_id} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                      <h4 className="font-semibold text-gray-900">{gesture.title}</h4>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => toast.info('Premium upgrade coming soon!')}
                  className="w-full mt-3 gradient-brand text-white"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Unlock All Gestures
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // ==================== PARTNER GESTURES DRAWER ====================
  const renderPartnerGesturesDrawer = () => {
    if (!showPartnerGestures) return null;
    
    const visibleGestures = status.is_premium || status.is_trial_active 
      ? partnerGestures 
      : partnerGestures.slice(0, 1);
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center animate-in fade-in duration-200">
        <div className="bg-white rounded-t-3xl w-full max-h-[70vh] overflow-hidden animate-in slide-in-from-bottom duration-300 mb-[70px]">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
            <h3 className="text-lg font-bold text-gray-900">Gain Brownies</h3>
            <button onClick={() => setShowPartnerGestures(false)} className="p-2 -mr-1">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[calc(70vh-60px)] pb-6">
            <p className="text-gray-500 text-sm mb-4">
              Gestures {status.partner.name.split(' ')[0]} wants you to do
            </p>
            
            {visibleGestures.length === 0 ? (
              <div className="text-center py-8">
                <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No gestures from your partner yet!</p>
                <p className="text-gray-400 text-sm">Ask them to create some gestures</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visibleGestures.map((gesture) => (
                  <div
                    key={gesture.trade_id}
                    className={`p-4 rounded-xl border ${
                      gesture.is_glowing 
                        ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-200' 
                        : 'border-gray-100 bg-white'
                    }`}
                  >
                    {gesture.is_glowing && (
                      <div className="flex items-center gap-1 text-yellow-600 text-xs font-medium mb-2">
                        <Bell className="w-3 h-3" />
                        <span>Your partner really wants this!</span>
                      </div>
                    )}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{gesture.title}</h4>
                        {gesture.description && (
                          <p className="text-sm text-gray-500 mt-1">{gesture.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded-full">
                        <Star className="w-3 h-3 text-green-600" fill="currentColor" />
                        <span className="text-sm font-bold text-green-700">+{gesture.points_value}</span>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-400 mt-2">
                      Complete this gesture to earn {gesture.points_value} points!
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // ==================== MAIN PAGE ====================
  return (
    <MobileLayout>
      {showCelebration && renderCelebration()}
      {renderCompletionModal()}
      {renderCreateGestureModal()}
      {renderMyGesturesDrawer()}
      {renderPartnerGesturesDrawer()}
      
      <div className="p-5 space-y-5" data-testid="brownies-page">
        {/* Header with points */}
        <div className="text-center pt-4">
          <h1 className="text-2xl font-bold text-gradient">Browny Points</h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Star className="w-6 h-6 text-yellow-500" fill="currentColor" />
            <span className="text-3xl font-bold text-gray-900">{status.user_points}</span>
            <span className="text-gray-500">points</span>
          </div>
        </div>
        
        {/* Trial/Premium indicator */}
        {!status.is_premium && (
          <div className={`rounded-xl p-3 text-center ${
            status.is_trial_active ? 'bg-yellow-50' : 'bg-red-50'
          }`}>
            {status.is_trial_active ? (
              <>
                <p className="text-sm font-medium text-yellow-800">
                  🎉 {status.trial_days_remaining} days left in your free trial!
                </p>
                <div className="w-full bg-yellow-200 rounded-full h-1.5 mt-2">
                  <div 
                    className="bg-yellow-500 h-1.5 rounded-full" 
                    style={{ width: `${(status.trial_days_remaining / 7) * 100}%` }}
                  />
                </div>
              </>
            ) : (
              <div>
                <p className="text-sm font-medium text-red-800 mb-2">
                  Your free trial has ended
                </p>
                <Button
                  onClick={() => toast.info('Premium upgrade coming soon!')}
                  size="sm"
                  className="gradient-brand text-white"
                >
                  <Crown className="w-4 h-4 mr-1" />
                  Upgrade to Premium
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* Gestures Section - NOW ON TOP */}
        <div>
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5 text-pink-500" />
            Exchange Browny Points
          </h2>
          
          <div className="grid grid-cols-2 gap-3">
            {/* My Gestures (Spend) - GLOWS when no gestures yet */}
            <button
              onClick={() => setShowMyGestures(true)}
              className={`p-4 rounded-xl border bg-white transition-all active:scale-[0.98] ${
                myGestures.length === 0 
                  ? 'border-primary shadow-lg shadow-primary/20 animate-pulse' 
                  : 'border-gray-100 hover:border-primary hover:shadow-sm'
              }`}
            >
              <div className="w-14 h-14 rounded-full mx-auto mb-2 overflow-hidden border-2 border-primary">
                {status.user_picture ? (
                  <img src={status.user_picture} alt="You" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                )}
              </div>
              <p className="font-semibold text-gray-900 text-sm">Spend Brownies</p>
              <p className="text-xs text-gray-500">
                {myGestures.length === 0 ? 'Create your first!' : `${myGestures.length} gestures created`}
              </p>
            </button>
            
            {/* Partner Gestures (Gain) */}
            <button
              onClick={() => setShowPartnerGestures(true)}
              className="p-4 rounded-xl border border-gray-100 bg-white hover:border-primary hover:shadow-sm transition-all active:scale-[0.98]"
            >
              <div className="w-14 h-14 rounded-full mx-auto mb-2 overflow-hidden border-2 border-pink-400">
                {status.partner.picture ? (
                  <img src={status.partner.picture} alt={status.partner.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-pink-100 flex items-center justify-center">
                    <span className="text-2xl">💕</span>
                  </div>
                )}
              </div>
              <p className="font-semibold text-gray-900 text-sm">Gain Brownies</p>
              <p className="text-xs text-gray-500">{partnerGestures.length} gestures for you</p>
            </button>
          </div>
        </div>
        
        {/* Partner Points Display */}
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-300">
                {status.partner.picture ? (
                  <img src={status.partner.picture} alt={status.partner.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-pink-100 flex items-center justify-center text-lg">💕</div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600">{status.partner.name.split(' ')[0]}&apos;s points</p>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                  <span className="font-bold text-gray-900">{status.partner.points}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Earn Challenges Section - NOW BELOW */}
        <div>
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Earn Browny Points
          </h2>
          
          <div className="space-y-2">
            {/* Only show 3 manual challenges - all always available */}
            {challenges.map((challenge) => {
              const Icon = CHALLENGE_ICONS[challenge.id] || Star;
              
              return (
                <button
                  key={challenge.id}
                  onClick={() => setShowCompletion(challenge.id)}
                  className="w-full p-4 rounded-xl border transition-all text-left flex items-center gap-3 border-gray-100 bg-white hover:border-primary hover:shadow-sm active:scale-[0.98]"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-gray-900">
                      {challenge.name}
                    </p>
                    <p className="text-xs truncate text-gray-500">
                      {challenge.description}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-100">
                    <Star className="w-3 h-3 text-yellow-600" fill="currentColor" />
                    <span className="text-sm font-bold text-yellow-700">
                      {challenge.points}
                    </span>
                    {challenge.initiator_bonus && (
                      <span className="text-xs text-yellow-600">+{challenge.initiator_bonus}</span>
                    )}
                  </div>
                  
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
          </div>
          
          {/* Info about automatic points */}
          <div className="mt-3 p-3 bg-green-50 rounded-xl">
            <p className="text-xs text-green-700">
              <span className="font-semibold">💡 Tip:</span> You also earn points automatically when creating and completing calendar events!
            </p>
          </div>
        </div>
        
        {/* Premium Upsell (if not premium and trial ended) */}
        {!status.is_premium && !status.is_trial_active && (
          <div className="bg-gradient-brand rounded-xl p-5 text-center shadow-pink">
            <Crown className="w-10 h-10 text-white mx-auto mb-2" />
            <h3 className="text-white font-bold text-lg">Upgrade to Premium</h3>
            <p className="text-white/80 text-sm mt-1 mb-4">
              10x cheaper than a date, 10x better for the relationship
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white/20 rounded-lg p-3">
                <p className="text-white font-bold">$3.99</p>
                <p className="text-white/70 text-xs">/month per partner</p>
              </div>
              <div className="bg-white/20 rounded-lg p-3">
                <p className="text-white font-bold">$25</p>
                <p className="text-white/70 text-xs">/year per partner</p>
              </div>
            </div>
            <Button
              onClick={() => toast.info('Premium upgrade coming soon!')}
              className="w-full bg-white text-primary hover:bg-white/90"
            >
              Upgrade Now
            </Button>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

const Brownies = () => {
  return (
    <ProtectedRoute>
      <BrowniesContent />
    </ProtectedRoute>
  );
};

export default Brownies;
